/**
 * Company selection, as pure functions.
 *
 * Kept out of the components for the same reason `nav-model.ts` is: this is the
 * logic that decides what the user sees and where a switch lands them, and it is
 * far cheaper to test as data-in/data-out than by mounting a `<Dropdown>`. The
 * web suite runs in a node environment with no jsdom, so anything that needs a
 * DOM is effectively untested — see `nav-model.test.ts` for the same bargain.
 *
 * Nothing here talks to the network or reads a store. `resolveCompany` is given
 * the memberships and the current selection; what it returns is what the sider
 * footer, the top-bar chip and the switcher all render from.
 */
import type { CompanyRefDto } from '@cropmate/shared';

export interface ResolveCompanyInput {
  /** From `CurrentUserDto.companies` — every company this account may act in. */
  companies: readonly CompanyRefDto[];
  /** The client's stored selection, or `defaultCompanyId` on a cold start. */
  currentCompanyId: string | null;
}

export interface ResolvedCompany {
  /** The company in force, or `null` when there is nothing to be in. */
  current: CompanyRefDto | null;
  /** What the dropdown lists: active companies only, ordered by short name. */
  options: CompanyRefDto[];
  /** `false` ⇒ render a static label, not a dropdown. */
  canSwitch: boolean;
  /** The sider footer's value. Never an empty string. */
  label: string;
}

/** Shown where a company would be, when there is none. */
export const NO_COMPANY_LABEL = '—';

export function resolveCompany(input: ResolveCompanyInput): ResolvedCompany {
  // An inactive company is not selectable. It can still be the CURRENT one for a
  // moment — an Admin may deactivate it while somebody is working in it — and
  // that resolves to `null` here rather than throwing, so the shell degrades to
  // "no company" and the guard can route the user somewhere that explains it.
  const options = [...input.companies]
    .filter((c) => c.isActive)
    .sort((a, b) => a.shortName.localeCompare(b.shortName));

  const current =
    input.currentCompanyId === null
      ? null
      : (options.find((c) => c.id === input.currentCompanyId) ?? null);

  return {
    current,
    options,
    // One membership is not a choice. Rendering a dropdown that can only pick
    // what is already picked is noise, and a single-company account should not
    // have to learn that the system is multi-company at all.
    canSwitch: options.length > 1,
    label: current?.shortName ?? NO_COMPANY_LABEL,
  };
}

/**
 * May the caller switch to `companyId`?
 *
 * The client half of the tenancy gate. The server re-checks every request
 * against `company_membership` and answers 403 `E_NOT_A_MEMBER`; this only stops
 * the UI firing a request that cannot succeed, and stops a no-op switch firing
 * one at all.
 */
export function canSwitchTo(input: ResolveCompanyInput, companyId: string): boolean {
  const { options, current } = resolveCompany(input);
  if (current?.id === companyId) return false;
  return options.some((c) => c.id === companyId);
}

/**
 * Where a switch should land.
 *
 * Keep the SCREEN, drop the ROW. A user switching companies is doing the same
 * job somewhere else, so bouncing them to `landingRoute` every time loses their
 * place. But four routes carry a company-scoped UUID in the path —
 * `/sheets/$date/$teamId`, `/workers/$workerId`, `/rates/$workTypeId`,
 * `/payroll/$runId` — and that id belongs to the company they are leaving. It
 * would 404 in the new one, and worse, pasting it back is exactly the
 * cross-company probe the isolation tests assert against.
 *
 * Search params are dropped for the same reason and get it for free: several
 * carry company-scoped ids too (`?team=`, `?periodId=`, `?runId=`, `?worker=`),
 * and returning a bare pathname leaves them behind.
 *
 * No permission re-check is needed on the destination. That is the payoff of
 * "same roles in every company": a screen the user was on is a screen they can
 * still reach. Do not add a guard here.
 */
export function landingAfterSwitch(pathname: string, landingRoute: string): string {
  const path = (pathname.split('?')[0] ?? '').replace(/\/+$/, '') || '/';
  if (path === '/') return landingRoute;

  // Longest first, so `/reports/register` is not swallowed by `/reports`.
  const KEEP_WHOLE = [
    '/reports/daily-earnings',
    '/reports/reconciliation',
    '/reports/register',
    '/reports/variance',
    '/admin/companies',
    '/admin/users',
    '/admin/roles',
    '/admin/config',
    '/admin/audit',
    '/reference/components',
    '/work-types',
    '/documents',
    '/profile',
    '/casual',
    '/teams',
    '/reports',
  ];
  if (KEEP_WHOLE.includes(path)) return path;

  // Everything else keeps only its first segment, which is what strips the
  // company-scoped id: `/workers/<uuid>` → `/workers`.
  const first = path.split('/')[1];
  return first ? `/${first}` : landingRoute;
}
